import React, { useCallback } from 'react';

import useStoreTranslation from 'hooks/useStoreTranslation';
import { TravelType } from 'models/enums/travelType';
import { RequestedItem } from 'models/requestHistory';
import { getFormattedPrice } from 'utils/currency';
import { htmlT } from 'utils/generic';

import { Section, Text, PolicyStatus, DetailsButton, GoToArrow, Price } from './styles';

type Props = {
  item: RequestedItem;
  onClickShowAllDetails: () => void;
};

const DetailsContent = ({ item, onClickShowAllDetails }: Props) => {
  const { t } = useStoreTranslation();
  const outOfPolicy = item.breakRulesMessage && item.breakRulesMessage.length > 0;
  const isExtraCharge = item.type === TravelType.ExtraCharge;

  const translateMessage = useCallback(
    (msg) => {
      const text = `rulesMessage.${msg}`;
      return t(text) !== text ? t(text) : msg;
    },
    [t],
  );

  const formattedPrice = (value: number) => {
    const { formatted } = getFormattedPrice({ value, currency: item.currency, t });
    return formatted;
  };

  return (
    <>
      {item.requestedBy !== '' && (
        <Text>
          {t('requestCard.details.requestedBy')}: {item.requestedBy}
        </Text>
      )}

      {item.rejectedReason && (
        <Section>
          <Text>{t('requestCard.details.rejectedReason')}:</Text>
          <Text>{item.rejectedReason.name}</Text>
        </Section>
      )}

      {item.paymentFailure && item.paymentFailureReason && (
        <Section>
          <Text>{t('requestCard.details.paymentFailure')}:</Text>
          <Text>{item.paymentFailureReason}</Text>
        </Section>
      )}

      {item.justifyForBreakingRules && (
        <Section>
          <Text>{t('requestCard.details.travelJustify')}:</Text>
          <Text>{item.justifyForBreakingRules}</Text>
        </Section>
      )}

      {item.costCenter && item.costCenter.name && (
        <Section>
          <Text>{t('requestCard.details.costCenter')}:</Text>
          <Text>
            {item.costCenter.code} - {item.costCenter.name}
          </Text>
        </Section>
      )}

      {item.billingCompanyName && (
        <Section>
          <Text>{t('requestCard.details.billingCompanyName')}:</Text>
          <Text>{item.billingCompanyName}</Text>
        </Section>
      )}

      {!!item.lowestLogicalFareList?.length && (
        <Section>
          <Text>{t('requestCard.details.lowerPrices.title')}:</Text>
          {item.lowestLogicalFareList.map((fare, index) => (
            <Price key={`price-item-tag-${index}`}>
              {fare?.description
                ? htmlT(t, 'requestCard.details.lowerPrices.itemText', {
                    price: formattedPrice(fare?.price),
                    description: fare?.description,
                  })
                : formattedPrice(fare?.price)}
            </Price>
          ))}
        </Section>
      )}

      <Section>
        <PolicyStatus $valid={!outOfPolicy}>
          {outOfPolicy ? t('requestCard.details.policyStatus.invalid') : t('requestCard.details.policyStatus.valid')}
        </PolicyStatus>
        <ul>
          {item.breakRulesMessage?.map((message, index) => (
            <li key={index.toString()}>
              <Text>{translateMessage(message)}</Text>
            </li>
          ))}
          <li>
            <Text>
              <strong>
                {item.breakRuleReasonName
                  ? `${t('requestCard.details.policyStatus.reason')}: ${item.breakRuleReasonName}`
                  : ''}
              </strong>
            </Text>
          </li>
        </ul>
      </Section>

      {!isExtraCharge && (
        <DetailsButton onClick={onClickShowAllDetails}>
          {t('requestCard.details.showAllDetails')} <GoToArrow />
        </DetailsButton>
      )}
    </>
  );
};

export default DetailsContent;
